import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Toolbar, useGridApiContext, useGridSelector, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from '../../utils/gridToolbarParts.js';
import { CustomQuickFilter } from '../../utils/CustomQuickFilter.js';
import { usePersistedDataStore } from '../../../store.js';
import { useUser, getUserNameById } from '../../../UserContext.js';
import { createOfferContext } from '../../../../utils/offerContextUtils.js';
import { canMarkDoNotFileScar, generateEvidenceFileName, isCaseSettled, isOfferSent, isAssignmentComplete } from '../utils/helpers.js';
import TownViewToolbar from '../../../TownView/TownViewToolbar.js';
import { toast } from '../../../../common/toast';
import { createEmptyRowSelection, createRowSelectionFromIds } from '../../utils/gridRowSelectionModel.js';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import MultiSettlementDialog from '../../Settlement/MultiSettlementDialog.js';
import { refreshAlgorithmOffers } from '../../Settlement/settlementService.js';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DownloadButton from './DownloadButton.js';
import CaseGridToolbar from './CaseGridToolbar.js';
import logger from '../../../../utils/logger';
import { handleOffersAndWorkupsEmailWorkflow } from '../../Offers/offerService.js';
import CaseReviewDialogs from './CaseReviewDialogs.js';
import { useCancelOffers } from '../../CancelOffers/useCancelOffers.js';
import { useOfferOptionsStore } from '../../Offers/offerOptionsStore.js';
import {
  initiateTownPortalOffersFlow,
  TOWN_PORTAL_DELIVERY_METHODS,
  TownPortalOffersDialog,
  useTownPortalEmailPreview,
} from '../../Offers/TownPortalFlow/index.js';

// XLSX is dynamically imported when needed to reduce initial bundle size
import { handleUnifiedOfferAction } from '../services/unifiedOfferService.js';
import { useEmailPreviewDialog } from '../hooks/useEmailPreviewDialog.js';
import { useSettlementEmail } from '../../Settlement/settlementEmailHelper.js';
import IncludeSettlementXlsDialog from '../../Settlement/IncludeSettlementXlsDialog.js';
import { markOffersAsSent } from '../../Offers/offerService.js';
import { useGeneralizedEmailPreview } from '../../../../hooks/useGeneralizedEmailPreview.js';
import { batchSaveComps, groupTrainingCompsByCase } from '../../SaveComps/batchSaveComps.js';
import { getTrainingUsersInfo, groupTrainingCompsByCaseFiltered } from '../../common/trainingUtils.js';
import { updateOffersBatch } from '../services/realtimeOfferService.js';
import { applyOffersToWorkup } from '../services/setOffersToWorkupService.js';
import NyscefUploadModal from '../../NyscefUpload/NyscefUploadModal.js';
import EvidencePrintDialog from './printing/EvidencePrintDialog.js';
import DocRequestDialog from './DocRequestDialog.js';
import AdjournCasesDialog from './AdjournCasesDialog.js';
import axiosInstance from '../../../../fetchConfig/axiosConfig.js';
import { formatPortfolioReduction } from '../../MunicipalResults/utils.js';


// Helper function to detect platform and return appropriate shortcut
const getPlatformShortcut = (key) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  return isMac ? `Cmd+${key}` : `Ctrl+${key}`;
};

// Consistent styling for all toolbar icon buttons
const ICON_SX = {
  p: 0.75,
  m: 0,
  width: 32,
  height: 32,
  borderRadius: '4px',
  lineHeight: 1,
  '& .MuiSvgIcon-root': { fontSize: '1.2rem' },
};

// Uniform tooltip wrapper with consistent positioning
const UniformTooltip = ({ title, children, ...props }) => (
  <Tooltip
    title={title}
    placement="bottom"
    slotProps={{
      popper: { modifiers: [{ name: 'offset', options: { offset: [0, -5] } }] },
    }}
    disableInteractive
    disableFocusListener
    disableTouchListener
    {...props}
  >
    <span style={{ display: 'inline-flex' }}>{children}</span>
  </Tooltip>
);

// Move these outside the component
const stageOptions = [
  { value: 'all', label: 'All Stages' },
  { value: 'BAR', label: 'BAR' },
  { value: 'SCAR', label: 'SCAR' },
  { value: 'Art7', label: 'Art 7' },
];

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Unsettled', label: 'Active' },
  { value: 'Settled', label: 'Settled' },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
  },
};

// View-specific visibility configuration
const getViewConfig = (townView) => ({
  // Filter controls
  showStageFilter: !townView,
  showStatusFilter: true, // Always show status filter
  
  // Action buttons
  showColumnsButton: !townView,
  showDownloadButton: true, // Always show download
  showFinalizeButton: !townView, // Only show in non-town view
  showOfferRoundsToggle: !townView, // Temporarily hide in town view, update if want to reenable.
  
  // Tooltips
});



// the ref for the workup options is so dumb to be here. haven't even checked if its logical.
function CustomToolbar(props) {
  const { 
    fileName, 
    keys, 
    caseData, 
    negotiationObjVersion, 
    selectedRows, 
    rows, 
    setSelectionModel,
    villageMode, 
    finalizing,
    setFinalizing,
    statusFilter, 
    setStatusFilter, 
    activeStage,
    setActiveStage,
    onAssignmentClick,
    showRoundColumns,
    setShowRoundColumns,
    maxRounds,
    // AV toggle props
    avToggleActive,
    setAvToggleActive,
    // Undo/redo props
    canUndo,
    canRedo,
    onUndo,
    onRedo,
    // Workup regeneration props
    workupRegenerating,
    townCourtDateKeys,
    townCourtDateFilterKey,
    hasUnassignedCourtDate,
    onTownCourtDateFilterChange,
    columnLayout,
  } = props;
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const setNegotiationObj = usePersistedDataStore((state) => state.setNegotiationObj);
  const setNegotiationObjVersion = usePersistedDataStore((state) => state.setNegotiationObjVersion);
  const emailingOffers = usePersistedDataStore((state) => state.emailingOffers);
  const workupReportType = useOfferOptionsStore((state) => state.workupReportType);
  const setEmailingOffers = usePersistedDataStore((state) => state.setEmailingOffers);
  const workupRegeneratingByParcel = usePersistedDataStore((state) => state.workupRegeneratingByParcel);
  const courtDate = getNegotiationObj.CourtDate === 'All' ? null : getNegotiationObj.CourtDate;

  // Check if any workup is currently regenerating
  const isWorkupRegenerating = useMemo(() => {
    if (workupRegenerating !== undefined) {
      return workupRegenerating;
    }
    // Fallback: check if any parcel is regenerating
    return Object.values(workupRegeneratingByParcel || {}).some(Boolean);
  }, [workupRegenerating, workupRegeneratingByParcel]);
  

  const { user, canUseFeature, townView, canPreviewTownView, setTownView } = useUser();
  const viewConfig = useMemo(() => getViewConfig(townView), [townView]);
  const isTrainingUser = user?.userAccess?.level === 'Training';
  const isPropAdminUser = user?.userAccess?.level === 'propadmin';
  const getIsFetching = usePersistedDataStore((state) => state.isFetching);
  const stipsDownloadInFlight = usePersistedDataStore((state) => state.stipsDownloadInFlight);
  const isDataLoading = !rows?.length || getIsFetching;
  const apiRef = useGridApiContext();
  const filteredSortedRowIds = useGridSelector(apiRef, gridFilteredSortedRowIdsSelector);
  const displayedRows = useMemo(() => {
    const api = apiRef.current;
    if (!api?.getRow) return (rows || []).filter((row) => row && !row.loading);
    return filteredSortedRowIds.map((id) => api.getRow(id)).filter((row) => row && !row.loading);
  }, [apiRef, filteredSortedRowIds, rows]);
  const casesStillRunning = getIsFetching;
  const casesRunningTooltip = 'Cases are still running';
  // Use unified cancellation hook
  const {
    cancelDialogOpen: unifiedCancelDialogOpen,
    cancellingOffers,
    openCancelDialog,
    closeCancelDialog,
    handleConfirmCancel,
  } = useCancelOffers();
  
  const [rowsToCancel, setRowsToCancel] = useState([]);
  const [subsetWarningDialogOpen, setSubsetWarningDialogOpen] = useState(false);
  const [openCases, setOpenCases] = useState([]);
  const [sendSelectedConfirmDialogOpen, setSendSelectedConfirmDialogOpen] = useState(false);
  const [missingOffersDialogOpen, setMissingOffersDialogOpen] = useState(false);
  const [missingOffersList, setMissingOffersList] = useState([]);
  const [missingOffersWithWorkup, setMissingOffersWithWorkup] = useState([]);
  const [updatingOffersToWorkup, setUpdatingOffersToWorkup] = useState(false);
  
  // Settlement processing states
  const [settlingCases, setSettlingCases] = useState(false);
  const [sendingSettlementEmail, setSendingSettlementEmail] = useState(false);
  const [settlementXlsDialogOpen, setSettlementXlsDialogOpen] = useState(false);
  
  // Workup sending state
  const [sendingWorkups, setSendingWorkups] = useState(false);
  
  // Missing Workup Values Dialog
  const [missingWorkupDialogOpen, setMissingWorkupDialogOpen] = useState(false);
  const [missingWorkupList, setMissingWorkupList] = useState([]);
  
  // Workup options dialog (standard Send Offers menu)
  // Town portal send-offers dialog (validate first, then link or spreadsheet options)
  const [betaSendOffersDialogOpen, setBetaSendOffersDialogOpen] = useState(false);
  
  // Track which flow is active for subset warning handling
  const [activeEmailFlow, setActiveEmailFlow] = useState(null); // 'regular', 'combined', or 'beta'
  const sendOffersSkipSubsetRef = useRef(false);
  const sendOffersSkipWorkupsAboveIfmvRef = useRef(false);
  const [workupsAboveIfmvDialogOpen, setWorkupsAboveIfmvDialogOpen] = useState(false);
  const [workupsAboveIfmvList, setWorkupsAboveIfmvList] = useState([]);

  // Same pattern as EvidenceComparisonTable: workupType is fixed for the flow (subset retry uses ref, not a second arg)
  const combinedOffersWorkupTypeRef = useRef('fnmaSummary');
  const combinedWorkupOptionsRef = useRef({ includeWorkups: true, type: 'batch' });
  
  // MUI dialog for offer resend confirmation
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [alreadySentCount, setAlreadySentCount] = useState(0);
  const [resendCallback, setResendCallback] = useState(null);
  
  const [multiSettlementOpen, setMultiSettlementOpen] = useState(false);

  const [uploadEvidenceDialogOpen, setUploadEvidenceDialogOpen] = useState(false);
  const [evidencePrintDialogOpen, setEvidencePrintDialogOpen] = useState(false);
  const [docRequestDialogOpen, setDocRequestDialogOpen] = useState(false);
  const [adjournDialogOpen, setAdjournDialogOpen] = useState(false);
  const [setOffersToWorkupDialogOpen, setSetOffersToWorkupDialogOpen] = useState(false);
  const [keepManuallySetOffers, setKeepManuallySetOffers] = useState(true);
  const [settingOffersToWorkup, setSettingOffersToWorkup] = useState(false);
  const [generatingAlgorithmicOffers, setGeneratingAlgorithmicOffers] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const downloadRef = useRef(null);

  // Training indicator/dialog state
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [trainingDialogLoading, setTrainingDialogLoading] = useState(false);
  const [selectedTrainingUserId, setSelectedTrainingUserId] = useState(null);
  const [updateOffersToo, setUpdateOffersToo] = useState(false);

  // Determine presence of training comps/offers across any case
  const { hasTrainingComps, hasTrainingOffers } = useMemo(() => {
    try {
      const cases = Array.isArray(getNegotiationObj?.cases) ? getNegotiationObj.cases : (Array.isArray(caseData) ? caseData : []);
      let comps = false;
      let offers = false;
      for (const c of cases) {
        if (!comps) {
          const tc = c?.TrainingComps;
          if (Array.isArray(tc) && tc.length > 0) {
            // Any entry with any truthy comp pid counts
            comps = tc.some((entry) => {
              const compMap = entry?.Comps || {};
              return Object.values(compMap).some((pid) => !!pid);
            });
          }
        }
        if (!offers) {
          const to = c?.TrainingOffers;
          offers = Array.isArray(to) && to.length > 0;
        }
        if (comps && offers) break;
      }
      return { hasTrainingComps: comps, hasTrainingOffers: offers };
    } catch (_e) {
      return { hasTrainingComps: false, hasTrainingOffers: false };
    }
  }, [getNegotiationObj?.cases, caseData]);

  // Extract training user information
  const trainingUsersInfo = useMemo(() => {
    const cases = Array.isArray(getNegotiationObj?.cases) ? getNegotiationObj.cases : (Array.isArray(caseData) ? caseData : []);
    return getTrainingUsersInfo(cases, getUserNameById);
  }, [getNegotiationObj?.cases, caseData]);

  // Group training comps by case (parcel_id) - filter by selected user if multiple users exist
  const trainingCompsByCase = useMemo(() => {
    const cases = Array.isArray(getNegotiationObj?.cases) ? getNegotiationObj.cases : (Array.isArray(caseData) ? caseData : []);
    
    // If multiple training users and a user is selected, filter to only that user's comps
    if (trainingUsersInfo.count > 1 && selectedTrainingUserId) {
      return groupTrainingCompsByCaseFiltered(cases, selectedTrainingUserId);
    }
    
    // Otherwise, use the standard grouping function (includes all users)
    return groupTrainingCompsByCase(cases);
  }, [getNegotiationObj?.cases, caseData, trainingUsersInfo.count, selectedTrainingUserId]);

  const collectTrainingOffersList = useCallback((filterByUserId = null) => {
    try {
      const cases = Array.isArray(getNegotiationObj?.cases) ? getNegotiationObj.cases : (Array.isArray(caseData) ? caseData : []);
      const list = [];
      cases.forEach((c) => {
        const parcelId = c?.parcel_id;
        const tos = Array.isArray(c?.TrainingOffers) ? c.TrainingOffers : [];
        tos.forEach((offer) => {
          // Filter by user ID if provided (Negotiator field corresponds to user ID)
          if (filterByUserId && offer?.Negotiator !== filterByUserId) {
            return;
          }
          list.push({ parcel_id: parcelId, ...offer });
        });
      });
      return list;
    } catch (_e) {
      return [];
    }
  }, [getNegotiationObj?.cases, caseData]);

  // Show resend dialog function
  const showResendDialog = (count, callback) => {
    setAlreadySentCount(count);
    setResendCallback(() => callback);
    setResendDialogOpen(true);
  };

  // Handle resend confirmation
  const handleResendConfirm = (selectedRound) => {
    setResendDialogOpen(false);
    if (resendCallback) {
      resendCallback(true, selectedRound);
      setResendCallback(null);
    }
  };

  
  // This is an insanely convoluted ai-idiocy flow that needs to be cleaned up. but... as usual... it works for now.
  // MUI dialog for email preview
  const { showDialog: showEmailPreviewDialog, dialogComponent: emailPreviewDialogComponent } = useEmailPreviewDialog({
    fileName,
    keys,
    caseData,
    townView,
    villageMode,
    statusFilter,
    activeStage,
    isDataLoading,
    selectedRows
  });

  // Settlement email service
  const { showSettlementEmailPreview, SettlementEmailDialog } = useSettlementEmail();
  
  // Offer email service
  
  // Generalized email preview for pre-processed data
  const { showDialog: showGeneralizedEmailPreview, dialogComponent: GeneralizedEmailDialog, sendEmailFromPreview } = useGeneralizedEmailPreview();

  const {
    showDialog: showTownPortalEmailPreview,
    dialogComponent: TownPortalEmailDialog,
    sendEmailFromPreview: sendTownPortalEmailFromPreview,
  } = useTownPortalEmailPreview();
  
  // Tooltip states
  // const [assignTooltipOpen, setAssignTooltipOpen] = useState(false);
  

  const handleAssignClick = () => {
    onAssignmentClick();
  };

  // Check if we should show "Email Offers" button
  // Priority: Finalize > Email > Cancel
  const areAllSelectedOffersFinalized = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) {
      return false;
    }

    // Settled cases are already resolved and do not block marking the rest as reviewed.
    const rowsToReview = selectedRows.filter((row) => !row._isSettled && !isCaseSettled(row));
    if (rowsToReview.length === 0) {
      return false;
    }
    
    // First priority: Check if ANY unsettled case is not finalized (CaseReview !== true)
    // If so, we should show "Finalize Offers" instead of "Email Offers"
    const hasUnfinalizedCases = rowsToReview.some((row) => {
      const negotiationCase = getNegotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
      
      if (!negotiationCase) {
        return true; // Treat missing case as unfinalized
      }
      
      return !isAssignmentComplete(negotiationCase, 'CaseReview');
    });
    
    if (hasUnfinalizedCases) {
      return false; // Show "Finalize Offers" if any case is not finalized
    }
    
    // Second priority: Check if all offers are already sent (should show Cancel Offer instead)
    const allOffersSent = rowsToReview.every(row => {
      const negotiationCase = getNegotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
      return negotiationCase && isOfferSent(negotiationCase.Negotiations, row.parcel_id);
    });
    
    if (allOffersSent) {
      return false; // Don't show Email Offers if all are already sent
    }
    
    // Third priority: All cases are finalized but not all are sent - show "Email Offers"
    return true;
  }, [selectedRows, getNegotiationObj, negotiationObjVersion]);

  const selectedCasesAreReviewed = useMemo(() => {
    if (!selectedRows?.length) return false;
    return selectedRows.every((row) => {
      if (isCaseSettled(row)) return true;
      const negotiationCase = getNegotiationObj.cases?.find((c) => c.parcel_id === row.parcel_id);
      return isAssignmentComplete(negotiationCase, 'CaseReview');
    });
  }, [selectedRows, getNegotiationObj, negotiationObjVersion]);

  // Check if all selected offers are livoffers, settled, or finalized.
  const shouldShowCancelOffer = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) {
      return false;
    }
    
    return selectedRows.every(row => {
      const negotiationCase = getNegotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
      
      if (!negotiationCase) {
        return false;
      }
      
      // Determines that the offer was sent by liveOffer = 1
      return isOfferSent(negotiationCase.Negotiations, row.parcel_id) || row._isSettled || isAssignmentComplete(negotiationCase || row, 'CaseReview');
    });
  }, [selectedRows, getNegotiationObj, negotiationObjVersion]);

  // Check if any selected cases are settled
  const hasSettledCasesSelected = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) {
      return false;
    }
    
    return selectedRows.some(row => row._isSettled === true);
  }, [selectedRows]);

  // Check if ALL selected cases are settled (required for settlement email functionality)
  const allCasesSettled = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) {
      return false;
    }
    
    return selectedRows.every(row => row._isSettled === true);
  }, [selectedRows]);

  const handleCancelOffer = () => {
    // Check if user has permission to cancel offers
    if (!canUseFeature('cancel_offers')) {
      toast.error('You do not have permission to cancel offers. This action is restricted.');
      return;
    }
    
    // Get the rows that will be processed (selected rows if any, otherwise all)
    const rowsToProcess = selectedRows.length > 0 ? selectedRows : rows;
    
    if (rowsToProcess.length === 0) {
      toast.error('No cases available to cancel offers for.');
      return;
    }

    // Store the rows to cancel and open the unified dialog
    setRowsToCancel(rowsToProcess);
    openCancelDialog();
  };


  const applySendOffersValidationResult = useCallback((result) => {
    if (result.canOpenDialog) {
      setBetaSendOffersDialogOpen(true);
      return;
    }
    if (result.requiresMissingOffersDialog) {
      setMissingOffersList(result.missingOffers.map((offer) => offer.Address));
      setMissingOffersWithWorkup(result.missingOffers);
      setMissingOffersDialogOpen(true);
      return;
    }
    if (result.requiresMissingWorkupDialog) {
      setMissingWorkupList(
        result.missingWorkupValues.map((caseItem) => ({
          address: caseItem.Address,
          workupValue: caseItem.ProprietyWorkup,
          rowNumber: caseItem._rowNumber,
        }))
      );
      setMissingWorkupDialogOpen(true);
      return;
    }
    if (result.requiresSubsetWarning) {
      setActiveEmailFlow('beta');
      setOpenCases(result.openCases);
      setSubsetWarningDialogOpen(true);
      return;
    }
    if (result.requiresWorkupsAboveIfmvDialog) {
      setActiveEmailFlow('beta');
      setWorkupsAboveIfmvList(result.workupsAboveIfmv || []);
      setWorkupsAboveIfmvDialogOpen(true);
      return;
    }
    toast.error(result.error || 'Cannot send offers for the current selection.');
  }, []);

  const handleInitiateSendOffers = useCallback((resetSkips = true) => {
    if (!selectedRows?.length) {
      toast.error('Select at least one case to send offers');
      return;
    }

    if (resetSkips) {
      sendOffersSkipSubsetRef.current = false;
      sendOffersSkipWorkupsAboveIfmvRef.current = false;
      setActiveEmailFlow(null);
    }

    const result = initiateTownPortalOffersFlow({
      selectedRows,
      allRows: rows,
      negotiationObj: getNegotiationObj,
      user,
      skipSubsetCheck: sendOffersSkipSubsetRef.current,
      skipWorkupsAboveIfmvCheck: sendOffersSkipWorkupsAboveIfmvRef.current,
    });

    applySendOffersValidationResult(result);
  }, [selectedRows, rows, getNegotiationObj, user, applySendOffersValidationResult]);

  // Unified wrapper function for all offer actions
  const handleUnifiedOfferWrapper = async (action, options = {}) => {
    logger.log('🔄 [CUSTOM TOOLBAR] handleUnifiedOfferWrapper called:', {
      action,
      selectedRowsCount: (options.selectedRows || selectedRows)?.length,
      allRowsCount: (options.allRows || rows)?.length,
      skipSubsetCheck: options.skipSubsetCheck,
      timestamp: new Date().toISOString()
    });
    
    // Set processing states based on action
    const isSending = action === 'send' || action === 'finalizeAndSend';
    const isFinalizing = action === 'finalize' || action === 'finalizeAndSend';
    
    if (isSending) setEmailingOffers(true);
    if (isFinalizing) setFinalizing(true);
    
    try {
      const result = await handleUnifiedOfferAction({
        selectedRows: options.selectedRows || selectedRows,
        allRows: options.allRows || rows,
        showResendDialog,
        showEmailPreviewDialog,
        s3UploadResult: null, // No longer using S3 upload callbacks
        user,
        action,
        skipSubsetCheck: options.skipSubsetCheck || false,
        skipWorkupsAboveIfmvCheck: options.skipWorkupsAboveIfmvCheck || false,
        skipOfferUpdate: options.skipOfferUpdate || false,
      });

      logger.log('📊 [CUSTOM TOOLBAR] handleUnifiedOfferAction result:', {
        success: result.success,
        hasEmailData: !!result.emailData,
        hasEmailOptions: !!result.emailOptions,
        action: result.action,
        message: result.message,
        timestamp: new Date().toISOString()
      });

      if (result.success) {
        // Show additional toast if offers were adjusted for 25% reduction rule
        if (result.adjustedCount && result.adjustedCount > 0) {
          logger.log(`${result.adjustedCount} offer(s) were adjusted to meet the 75% IFMV minimum threshold`);
          // toast.info(`${result.adjustedCount} offer(s) were adjusted to meet the 75% IFMV minimum threshold`);
        }
        
        // Show email preview when we have email data
        if (result.emailData && result.emailOptions && action === 'send') {
          logger.log('✅ [CUSTOM TOOLBAR] Showing email preview');
          logger.log('📧 [CUSTOM TOOLBAR] Email data details:', {
            emailDataCount: result.emailData?.length,
            emailOptionsKeys: Object.keys(result.emailOptions || {}),
            templateType: result.emailOptions?.templateType,
            dataCount: result.emailOptions?.data?.length,
            timestamp: new Date().toISOString()
          });
          
          // Show the email preview dialog using the generalized email preview with pre-processed data
          logger.log('🔄 [CUSTOM TOOLBAR] Calling showGeneralizedEmailPreview...');
          await showGeneralizedEmailPreview(result.emailOptions, async (confirmed, emailPreviewData) => {
            logger.log('🎯 [CUSTOM TOOLBAR] Offer email onComplete callback called:', { 
              confirmed, 
              hasEmailPreviewData: !!emailPreviewData,
              timestamp: new Date().toISOString()
            });
            
            if (confirmed && emailPreviewData) {
              logger.log('🔄 [CUSTOM TOOLBAR] User confirmed, sending email...');
              try {
                const sendResult = await sendEmailFromPreview(emailPreviewData);
                logger.log('📧 [CUSTOM TOOLBAR] Email send result:', sendResult);
                
                if (sendResult.success) {
                  // Only update LiveOffer to 1 (mark as sent) and case status if email was successfully sent
                  try {
                    logger.log('🔄 [CUSTOM TOOLBAR] Email sent successfully, marking offers as sent...');
                    
                    // Get the negotiation object and extract parcel IDs from email data
                    const negotiationObj = usePersistedDataStore.getState().negotiationObj;
                    const emailData = emailPreviewData.emailData;
                    
                    if (emailData && emailData.data && Array.isArray(emailData.data)) {
                      // Extract parcel IDs from email data
                      const parcelIds = emailData.data
                        .filter(item => item.parcel_id)
                        .map(item => item.parcel_id);
                      
                      if (parcelIds.length > 0) {
                        // Use the centralized function to mark offers as sent and update case status
                        const markResult = await markOffersAsSent(parcelIds, negotiationObj, user);
                        
                        if (markResult.success) {
                          logger.log(`✅ [CUSTOM TOOLBAR] ${markResult.message}`);
                        } else {
                          logger.warn('⚠️ [CUSTOM TOOLBAR] Failed to mark offers as sent:', markResult.error);
                          // Don't fail the entire operation if offer update fails
                        }
                      }
                    }
                  } catch (error) {
                    logger.error('❌ [CUSTOM TOOLBAR] Error marking offers as sent:', {
                      error: error.message,
                      stack: error.stack
                    });
                    // Don't fail the entire operation if offer update fails
                  }
                  
                  toast.success('Offer email sent successfully');
                } else {
                  logger.error('❌ [CUSTOM TOOLBAR] Email send failed:', sendResult.error);
                  toast.error(`Failed to send email: ${sendResult.error}`);
                }
              } catch (error) {
                logger.error('❌ [CUSTOM TOOLBAR] Error sending email:', error);
                toast.error(`Error sending email: ${error.message}`);
              }
            } else if (confirmed) {
              logger.log('⚠️ [CUSTOM TOOLBAR] User confirmed but no email data available');
              toast.warning('No email data available to send');
            }
          });
          logger.log('✅ [CUSTOM TOOLBAR] showGeneralizedEmailPreview completed');
        } else {
          // Show success message for other actions
          logger.log('📝 [CUSTOM TOOLBAR] Showing success message:', result.message);
          toast.success(result.message);
        }
      } else if (result.requiresMissingOffersDialog) {
        // Handle missing offers dialog
        setMissingOffersList(result.missingOffers.map(offer => offer.Address));
        // Include ALL missing offers, not just those with workup values > 0
        // The handleSetOffersToWorkup function will check for cases with no workup values
        setMissingOffersWithWorkup(result.missingOffers);
        setMissingOffersDialogOpen(true);
      } else if (result.requiresMissingWorkupDialog) {
        // Handle missing workup values dialog
        setMissingWorkupList(result.missingWorkupValues.map(caseItem => ({
          address: caseItem.Address,
          workupValue: caseItem.ProprietyWorkup,
          rowNumber: caseItem._rowNumber
        })));
        setMissingWorkupDialogOpen(true);
      } else if (result.requiresSubsetWarning) {
        // Handle subset warning dialog - track that we're in regular flow
        setActiveEmailFlow('regular');
        setOpenCases(result.openCases);
        setSubsetWarningDialogOpen(true);
      } else if (result.requiresWorkupsAboveIfmvDialog) {
        if (!activeEmailFlow) setActiveEmailFlow('regular');
        setWorkupsAboveIfmvList(result.workupsAboveIfmv || []);
        setWorkupsAboveIfmvDialogOpen(true);
      } else if (result.requiresFinalization) {
        // Handle finalization required error
        toast.error(result.error);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      logger.error(`Error in unified ${action}:`, error);
      toast.error(error.message);
    } finally {
      // Reset processing states based on action
      if (isSending) setEmailingOffers(false);
      if (isFinalizing) setFinalizing(false);
    }
  };

  const handleSettlementEmailWrapper = async (includeXls) => {
    try {
      await showSettlementEmailPreview({
        selectedRows,
        allRows: rows,
        negotiationObj: getNegotiationObj,
        includeXls,
        onComplete: (confirmed, emailData) => {
          logger.log('🎯 [CUSTOM TOOLBAR] onComplete callback called:', { confirmed, emailData });
          if (confirmed) {
            toast.success('Settlement email sent successfully');
          } else if (emailData?.error) {
            toast.error(emailData.error);
          }
          setSendingSettlementEmail(false);
        }
      });
    } catch (error) {
      logger.error('❌ [CUSTOM TOOLBAR] Error in settlement email:', error);
      toast.error(error.message);
      setSendingSettlementEmail(false);
    }
  };

  const handleBetaSendOffersConfirm = async (payload) => {
    setBetaSendOffersDialogOpen(false);
    const skipSubsetCheck = sendOffersSkipSubsetRef.current;
    const skipWorkupsAboveIfmvCheck = sendOffersSkipWorkupsAboveIfmvRef.current;
    sendOffersSkipSubsetRef.current = false;
    sendOffersSkipWorkupsAboveIfmvRef.current = false;

    if (payload.deliveryMethod === TOWN_PORTAL_DELIVERY_METHODS.LINK) {
      if (!selectedRows?.length) {
        toast.error('Select at least one case to send offers');
        return;
      }

      const parcelIds = selectedRows.map((row) => row.parcel_id).filter(Boolean);
      setEmailingOffers(true);

      try {
        await showTownPortalEmailPreview(
          {
            parcelIds,
            linkOptions: payload.linkOptions,
            negotiationObj: getNegotiationObj,
            user,
          },
          async (confirmed, emailPreviewData) => {
            if (confirmed && emailPreviewData) {
              try {
                const sendResult = await sendTownPortalEmailFromPreview(emailPreviewData);
                logger.log('📧 [CUSTOM TOOLBAR] Town portal offer email send result:', sendResult);
                if (sendResult.success) {
                  const markResult = await markOffersAsSent(parcelIds, getNegotiationObj, user);
                  logger.log('📧 [CUSTOM TOOLBAR] Mark offers as sent result:', markResult);
                  if (markResult.success) {
                    toast.success('Offers sent successfully');
                  } else {
                    toast.warning(markResult.error || 'Email sent but failed to update offer status');
                  }
                } else {
                  toast.error(`Failed to send email: ${sendResult.error}`);
                }
              } catch (error) {
                logger.error('Error sending town portal offer email:', error);
                toast.error(error.message || 'Failed to send town portal offer email');
              }
            }
            setEmailingOffers(false);
          }
        );
      } catch (error) {
        logger.error('Error opening town portal email preview:', error);
        toast.error(error.message || 'Failed to open email composer');
        setEmailingOffers(false);
      }
      return;
    }

    if (!payload.workupOptions.includeWorkups) {
      await handleUnifiedOfferWrapper('send', { skipSubsetCheck, skipWorkupsAboveIfmvCheck });
      return;
    }
    await handleSendOffersAndWorkups(payload.workupReportType, payload.workupOptions, skipSubsetCheck, skipWorkupsAboveIfmvCheck);
  };

  /** Internal runner: skipSubsetCheck only (workupType from combinedOffersWorkupTypeRef — like Evidence ctx.rerun). */
  const runCombinedOffersWorkupsEmail = async (skipSubsetCheck, skipWorkupsAboveIfmvCheck = false) => {
    if (!selectedRows || selectedRows.length === 0) {
      toast.error('Select at least one case to send offers and workups');
      return;
    }

    const workupType = combinedOffersWorkupTypeRef.current;

    setSendingWorkups(true);

    try {
      const result = await handleOffersAndWorkupsEmailWorkflow({
        selectedRows,
        allRows: rows,
        negotiationObj: getNegotiationObj,
        user,
        workupType,
        skipSubsetCheck,
        skipWorkupsAboveIfmvCheck,
        workupOptions: combinedWorkupOptionsRef.current,
      });
      
      logger.log('📊 [CUSTOM TOOLBAR] Combined workflow result:', result);

      if (result.success) {
        // Show email preview using generalized email preview
        await showGeneralizedEmailPreview(result.emailOptions, async (confirmed, emailPreviewData) => {
          logger.log('🎯 [CUSTOM TOOLBAR] Combined email onComplete callback:', { 
            confirmed, 
            hasEmailPreviewData: !!emailPreviewData 
          });
          
          if (confirmed && emailPreviewData) {
            try {
              const sendResult = await sendEmailFromPreview(emailPreviewData);
              logger.log('📧 [CUSTOM TOOLBAR] Combined email send result:', sendResult);
              
              if (sendResult.success) {
                // Mark offers as sent after successful email
                try {
                  const emailData = emailPreviewData.emailData;
                  
                  if (emailData && emailData.data && Array.isArray(emailData.data)) {
                    const parcelIds = emailData.data
                      .filter(item => item.parcel_id)
                      .map(item => item.parcel_id);
                    
                    if (parcelIds.length > 0) {
                      const markResult = await markOffersAsSent(parcelIds, getNegotiationObj, user);
                      
                      if (markResult.success) {
                        logger.log(`✅ [CUSTOM TOOLBAR] ${markResult.message}`);
                      } else {
                        logger.warn('⚠️ [CUSTOM TOOLBAR] Failed to mark offers as sent:', markResult.error);
                      }
                    }
                  }
                } catch (error) {
                  logger.error('❌ [CUSTOM TOOLBAR] Error marking offers as sent:', error);
                }
                
                {
                  const wo = combinedWorkupOptionsRef.current;
                  const batchAttach = wo.includeWorkups && wo.type === 'batch';
                  toast.success(
                    batchAttach
                      ? 'Offers and workups email sent successfully'
                      : wo.type === 'individual'
                        ? 'Offer email sent successfully (workup links in spreadsheet)'
                        : 'Offer email sent successfully'
                  );
                }
              } else {
                toast.error(`Failed to send email: ${sendResult.error}`);
              }
            } catch (error) {
              logger.error('❌ [CUSTOM TOOLBAR] Error sending combined email:', error);
              toast.error(`Error sending email: ${error.message}`);
            }
          }
          setSendingWorkups(false);
        });
      } else {
        // Handle validation failures
        setSendingWorkups(false);
        
        if (result.validationType === 'subset_warning') {
          // Track that we're in combined flow for subset warning handling
          setActiveEmailFlow('combined');
          setOpenCases(result.openCasesNotSelected || []);
          setSubsetWarningDialogOpen(true);
        } else if (result.validationType === 'missing_offers') {
          // Show missing offers dialog
          setMissingOffersList(result.missingOffers.map(offer => offer.Address));
          setMissingOffersWithWorkup(result.missingOffers);
          setMissingOffersDialogOpen(true);
        } else if (result.validationType === 'missing_workup_values') {
          // Show missing workup values dialog - transform to expected format
          setMissingWorkupList(result.missingWorkupValues.map(caseItem => ({
            address: caseItem.Address,
            workupValue: caseItem.ProprietyWorkup,
            rowNumber: caseItem._rowNumber
          })));
          setMissingWorkupDialogOpen(true);
        } else if (result.validationType === 'workups_above_ifmv') {
          setActiveEmailFlow('combined');
          setWorkupsAboveIfmvList(result.workupsAboveIfmv || []);
          setWorkupsAboveIfmvDialogOpen(true);
        } else {
          // Generic error toast
          toast.error(result.error || 'Failed to prepare combined email');
        }
      }
    } catch (error) {
      logger.error('❌ [CUSTOM TOOLBAR] Error in combined offers + workups flow:', error);
      toast.error(error.message || 'Failed to send offers and workups');
      setSendingWorkups(false);
    }
  };

  /** @param {string} [workupType='fnmaSummary'] @param {{ includeWorkups?: boolean, type?: string }} [workupOptions] @param {boolean} [skipSubsetCheck=false] */
  const handleSendOffersAndWorkups = async (workupType = 'fnmaSummary', workupOptions = null, skipSubsetCheck = false, skipWorkupsAboveIfmvCheck = false) => {
    combinedOffersWorkupTypeRef.current = workupType;
    combinedWorkupOptionsRef.current = workupOptions ?? { includeWorkups: true, type: 'batch' };
    await runCombinedOffersWorkupsEmail(skipSubsetCheck, skipWorkupsAboveIfmvCheck);
  };

  const handleAlgorithmicOffers = async () => {
    const uids = (selectedRows || []).map((row) => row.parcel_id).filter(Boolean);
    const municode = getNegotiationObj?.MuniCode;
    const taxYear = getNegotiationObj?.TaxYear;
    const muniLength = String(municode || '').length;
    if (!uids.length) {
      toast.error('Select at least one case');
      return;
    }
    if ((muniLength !== 3 && muniLength !== 5) || !taxYear) {
      toast.error('A municipality or village and tax year are required');
      return;
    }
    setGeneratingAlgorithmicOffers(true);
    try {
      const { data } = await axiosInstance.post('/algorithmic_offers', {
        uids,
        MuniCode: municode,
        TaxYear: taxYear,
        UserId: user?.userId,
        WorkupType: String(getNegotiationObj?.reportType || '').toLowerCase() === 'assessment' ? 'assessment' : 'sales',
      });
      const rows = data.cases || [];
      if (rows.length) {
        const XLSX = await import('xlsx');
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows, { header: data.columns }), 'Algorithmic Offers');
        XLSX.writeFile(book, `algorithmic_offers_${municode}_${taxYear}.xlsx`);
      }
      const requested = data.portfolio?.requested_reductions || {};
      const prior = data.portfolio?.prior_reductions;
      const winLine = (row) => row
        ? `${formatPortfolioReduction(row.reduction)} | ${row.wins ?? 0}/${row.cases ?? 0} wins`
        : 'none';
      logger.log('[algorithmic offers] prior performance', {
        past2Years: winLine(prior?.past_2_years),
        years: (prior?.views || []).map((view) => `${view.label}: ${winLine(view)}`),
      });
      logger.log('[algorithmic offers] requested reductions', {
        starting: formatPortfolioReduction(requested.AlgorithmFirstOffer),
        firstRound: formatPortfolioReduction(requested.AlgorithmSecondOffer),
        ceiling: formatPortfolioReduction(requested.AlgorithmCeilingOffer),
      });
      toast.success(
        `Starting ${formatPortfolioReduction(requested.AlgorithmFirstOffer)}. First round ${formatPortfolioReduction(requested.AlgorithmSecondOffer)}. Ceiling ${formatPortfolioReduction(requested.AlgorithmCeilingOffer)}.`
      );
    } catch (error) {
      logger.error('Algorithmic offers failed', error);
      toast.error(error?.response?.data?.error || error.message || 'Failed to generate algorithmic offers');
    } finally {
      setGeneratingAlgorithmicOffers(false);
    }
  };

  const handleRefreshAlgoOffers = async () => {
    setGeneratingAlgorithmicOffers(true);
    try {
      const count = await refreshAlgorithmOffers();
      toast.success(`Refreshed algo offers for ${count} open case${count === 1 ? '' : 's'}`);
    } catch (error) {
      logger.error('Refresh algo offers failed', error);
      toast.error(error?.response?.data?.error || error.message || 'Failed to refresh algo offers');
    } finally {
      setGeneratingAlgorithmicOffers(false);
    }
  };

  const setOffersToWorkupRows = (selectedRows?.length ? selectedRows : rows)
    .filter((row) => row && !row.loading && row.parcel_id);

  const handleConfirmSetOffersToWorkup = async () => {
    setSettingOffersToWorkup(true);
    try {
      const result = await applyOffersToWorkup({
        rows: setOffersToWorkupRows,
        negotiationCases: getNegotiationObj?.cases || [],
        keepManuallySetOffers,
        user,
      });
      if (!result.success) {
        toast.error(result.error || 'Failed to set offers to workup');
        return;
      }
      if (result.written.length === 0) {
        toast.info('No offers to update');
      } else {
        toast.success(`Set ${result.written.length} offer${result.written.length === 1 ? '' : 's'} to workup`);
      }
      setSetOffersToWorkupDialogOpen(false);
    } catch (error) {
      console.log('[SetOffersToWorkup] failed', error);
      toast.error(error.message || 'Failed to set offers to workup');
    } finally {
      setSettingOffersToWorkup(false);
    }
  };

  const isProcessing = emailingOffers || finalizing || cancellingOffers || settlingCases || sendingSettlementEmail || sendingWorkups || isWorkupRegenerating;
  const hasSelectedRows = selectedRows.length > 0;
  const isDisabled = isProcessing || isDataLoading || !hasSelectedRows || isTrainingUser;
  const controlsLocked = finalizing || isDataLoading || isWorkupRegenerating;
  const downloadsBlocked = isDataLoading || downloadLoading || isWorkupRegenerating;
  const showTraining = isPropAdminUser && (hasTrainingComps || hasTrainingOffers);
  const offerCountLabel = (one, many) => (selectedRows.length > 1 ? many : one);
  const selectCasesTip = hasSelectedRows ? '' : 'Select cases to perform actions';
  const hasUnfiledScarSelected = selectedRows.some((row) => row.SCARFiled !== 1);
  const hasMissingHearingDateSelected = selectedRows.some((row) => !row.SCARHearingDate && row.SCARDeterminationAction !== 'OA');

  const openTrainingDialog = () => {
    setTrainingDialogLoading(false);
    if (trainingUsersInfo.count === 1) {
      setSelectedTrainingUserId(trainingUsersInfo.userIds[0]);
    } else {
      setSelectedTrainingUserId(null);
    }
    setUpdateOffersToo(false);
    setTrainingDialogOpen(true);
  };

  const downloadsBlockedTitle = !downloadsBlocked
    ? ''
    : downloadLoading
      ? 'Downloading...'
      : isWorkupRegenerating
        ? 'Workups are regenerating'
        : casesStillRunning
          ? casesRunningTooltip
          : '';

  const actionsLabel = finalizing ? 'Finalizing...'
    : emailingOffers ? 'Emailing...'
    : cancellingOffers ? 'Cancelling...'
    : settlingCases ? 'Settling...'
    : sendingSettlementEmail ? 'Sending...'
    : (sendingWorkups || generatingAlgorithmicOffers) ? 'Generating...'
    : hasSelectedRows ? `Actions (${selectedRows.length})` : 'Actions';

  const handleSendSelectedClick = () => {
    setSubsetWarningDialogOpen(false);
    setSendSelectedConfirmDialogOpen(true);
  };

  // if the user specifically says i am sending a sub-selection of cases, skip the subset warning.
  // Also requires the skip supset to exist in teh ons3 upload callback. (dumb flow for the condoitional rendering to utilize download button like a functional invocation)
  const handleConfirmSendSelected = async () => {
    setSendSelectedConfirmDialogOpen(false);
    
    if (activeEmailFlow === 'beta') {
      sendOffersSkipSubsetRef.current = true;
      handleInitiateSendOffers(false);
      return;
    }

    // Route to appropriate flow based on which flow triggered the subset warning
    if (activeEmailFlow === 'combined') {
      await runCombinedOffersWorkupsEmail(true);
    } else {
      await handleUnifiedOfferWrapper('send', { skipSubsetCheck: true });
    }
  };

  const handleContinueWorkupsAboveIfmv = async () => {
    setWorkupsAboveIfmvDialogOpen(false);
    sendOffersSkipWorkupsAboveIfmvRef.current = true;
    sendOffersSkipSubsetRef.current = true;
    const flow = activeEmailFlow;
    setActiveEmailFlow(null);

    if (flow === 'beta') {
      handleInitiateSendOffers(false);
      return;
    }
    if (flow === 'combined') {
      await runCombinedOffersWorkupsEmail(true, true);
      return;
    }
    await handleUnifiedOfferWrapper('send', {
      skipSubsetCheck: true,
      skipWorkupsAboveIfmvCheck: true,
    });
  };

  // This is the "continue" flow after setting offers to workup values in the warning dialog
  const handleSetOffersToWorkup = async () => {
    setUpdatingOffersToWorkup(true);
    try {
      logger.log('🔍 [WORKUP] Setting workup values for offers:', missingOffersWithWorkup);
      
      // Check if there are any cases with no workup values (workup = 0) that cannot be set to workup values
      const casesWithNoWorkup = missingOffersWithWorkup.filter(offer => 
        !offer.ProprietyWorkup || offer.ProprietyWorkup === 0
      );
      
      if (casesWithNoWorkup.length > 0) {
        // Show missing workup values dialog for cases with no workup values
        setMissingWorkupList(casesWithNoWorkup.map(c => ({
          address: c.Address,
          workupValue: c.ProprietyWorkup
        })));
        setMissingOffersDialogOpen(false);
        setMissingWorkupDialogOpen(true);
        setUpdatingOffersToWorkup(false);
        return;
      }
      
      const result = await applyOffersToWorkup({
        rows: missingOffersWithWorkup,
        negotiationCases: getNegotiationObj?.cases || [],
        keepManuallySetOffers: false,
        user,
      });
      if (!result.success || result.written.length === 0) {
        toast.error(result.error || 'Failed to set offers to workup values and finalize. Please try again.');
        return;
      }

      await handleUnifiedOfferWrapper('finalize', { skipOfferUpdate: true });
      
      // Close the missing offers dialog after successful completion
      setMissingOffersDialogOpen(false);
      
    } catch (error) {
      logger.error('Error setting offers to workup values and finalizing:', error);
      toast.error('Failed to set offers to workup values and finalize. Please try again.');
    } finally {
      setUpdatingOffersToWorkup(false);
    }
  };

  const handleUnifiedCancelConfirm = async () => {
    await handleConfirmCancel(rowsToCancel);
  };

  // is this even used anymore?
  const handleSendAllFinalized = async () => {
    setSubsetWarningDialogOpen(false);
    
    // Update selection model to include all cases to prevent subset warning dialog from appearing again
    // Only include cases that are not already selected to avoid duplicates
    const currentSelectedIds = new Set(selectedRows.map(row => row.id));
    const additionalRowIds = rows
      .filter(row => !currentSelectedIds.has(row.id))
      .map(row => row.id);
    
    if (additionalRowIds.length > 0) {
      setSelectionModel(createRowSelectionFromIds([...selectedRows.map(row => row.id), ...additionalRowIds]));
    }

    if (activeEmailFlow === 'beta') {
      sendOffersSkipSubsetRef.current = true;
      setActiveEmailFlow(null);
      handleInitiateSendOffers(false);
      return;
    }
    
    // Combine selected rows with other open cases
    const allCasesToProcess = [...selectedRows, ...openCases];
    
    await handleUnifiedOfferWrapper('send', {
      selectedRows: allCasesToProcess,
      skipSubsetCheck: true
    });
  };

  if (townView) {
    return (
      <TownViewToolbar
        rows={rows}
        selectedRows={selectedRows}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        townCourtDateKeys={townCourtDateKeys}
        townCourtDateFilterKey={townCourtDateFilterKey}
        hasUnassignedCourtDate={hasUnassignedCourtDate}
        onTownCourtDateFilterChange={onTownCourtDateFilterChange}
        caseData={caseData}
        fileName={fileName}
        keys={keys}
        villageMode={villageMode}
        avToggleActive={avToggleActive}
        setAvToggleActive={setAvToggleActive}
        isDataLoading={isDataLoading}
        columnLayout={columnLayout}
      />
    );
  }

  return (
    <Toolbar sx={{ px: 1.5, py: 0.5, minHeight: 44, gap: 0, justifyContent: 'flex-start', width: '100%' }}>
      <CaseGridToolbar
        filters={(
          <>
            <GridToolbarColumnsButton columnLayout={columnLayout} />
            <GridToolbarFilterButton />
            {viewConfig.showStageFilter && (
              <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0, '& .MuiInputBase-root': { height: 32 } }}>
                <InputLabel id="stage-multiselect-label" sx={{ bgcolor: 'transparent !important', zIndex: '1 !important', pointerEvents: 'none' }}>
                  Stage
                </InputLabel>
                <Select
                  labelId="stage-multiselect-label"
                  id="stage-multiselect"
                  value={activeStage}
                  onChange={(e) => setActiveStage(e.target.value)}
                  input={<OutlinedInput label="Stage" />}
                  renderValue={(selected) => {
                    const found = stageOptions.find(opt => opt.value === selected);
                    return found ? found.label : selected;
                  }}
                  MenuProps={MenuProps}
                >
                  {stageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ py: 0 }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {viewConfig.showStatusFilter && (
              <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0, '& .MuiInputBase-root': { height: 32 } }}>
                <InputLabel id="status-multiselect-label" sx={{ bgcolor: 'transparent !important', zIndex: '1 !important', pointerEvents: 'none' }}>
                  Status
                </InputLabel>
                <Select
                  labelId="status-multiselect-label"
                  id="status-multiselect"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => {
                    const found = statusOptions.find(opt => opt.value === selected);
                    return found ? found.label : selected;
                  }}
                  MenuProps={MenuProps}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ py: 0 }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <CustomQuickFilter />
            {canUndo !== undefined && canRedo !== undefined && (
              <>
                <UniformTooltip title={`Undo (${getPlatformShortcut('Z')})${!canUndo ? ' - No changes to undo' : ''}`}>
                  <IconButton
                    disabled={!canUndo || isWorkupRegenerating}
                    onClick={onUndo}
                    sx={{
                      ...ICON_SX,
                      '& .MuiSvgIcon-root': { color: canUndo ? '#1976d2' : '#ccc' },
                      '&:hover': {
                        backgroundColor: canUndo ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '& .MuiSvgIcon-root': { color: canUndo ? '#1976d2' : '#ccc' }
                      },
                      '&:active .MuiSvgIcon-root': { color: canUndo ? '#1976d2' : '#ccc' },
                      '&.Mui-disabled': { backgroundColor: 'transparent !important' }
                    }}
                  >
                    <UndoIcon />
                  </IconButton>
                </UniformTooltip>
                <UniformTooltip title={`Redo (${getPlatformShortcut('Y')})${!canRedo ? ' - No changes to redo' : ''}`}>
                  <IconButton
                    disabled={!canRedo || isWorkupRegenerating}
                    onClick={onRedo}
                    sx={{
                      ...ICON_SX,
                      '& .MuiSvgIcon-root': { color: canRedo ? '#1976d2' : '#ccc' },
                      '&:hover': {
                        backgroundColor: canRedo ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '& .MuiSvgIcon-root': { color: canRedo ? '#1976d2' : '#ccc' }
                      },
                      '&:active .MuiSvgIcon-root': { color: canRedo ? '#1976d2' : '#ccc' },
                      '&.Mui-disabled': { backgroundColor: 'transparent !important' }
                    }}
                  >
                    <RedoIcon />
                  </IconButton>
                </UniformTooltip>
              </>
            )}
          </>
        )}
        selectedCount={selectedRows.length}
        avMode={Boolean(avToggleActive)}
        onAvModeChange={setAvToggleActive}
        avDisabled={controlsLocked}
        avTitle={avToggleActive ? 'AV Mode Active - Click to disable' : 'Assessed Value Mode'}
        showOfferRounds={Boolean(showRoundColumns)}
        onShowOfferRoundsChange={setShowRoundColumns}
        showRoundsToggle={viewConfig.showOfferRoundsToggle}
        roundsDisabled={controlsLocked || maxRounds < 1}
        roundsTitle={
          maxRounds < 1 ? 'No offer rounds available'
            : showRoundColumns ? 'Hide Offer Rounds' : 'Show Offer Rounds'
        }
        showDoNotFileScar={selectedRows.length > 0 && selectedRows.every(canMarkDoNotFileScar)}
        showTraining={showTraining}
        showAdminSettings={canPreviewTownView}
        actionsLabel={actionsLabel}
        actionsBusy={casesStillRunning || isProcessing || generatingAlgorithmicOffers}
        actionsDisabled={controlsLocked}
        actionsTitle={controlsLocked ? (isWorkupRegenerating ? 'Workups are regenerating' : casesStillRunning ? casesRunningTooltip : '') : ''}
        exportBusy={casesStillRunning || downloadLoading}
        exportDisabled={downloadsBlocked}
        exportTitle={downloadsBlockedTitle}
        handlers={{
          downloadCases: () => downloadRef.current?.downloadCases(),
          downloadWorkups: () => downloadRef.current?.downloadWorkups(),
          downloadStips: () => downloadRef.current?.downloadStips(),
          markReviewed: () => { void handleUnifiedOfferWrapper('finalize'); },
          flagJurisdictional: () => setDocRequestDialogOpen(true),
          flagCase: handleAssignClick,
          generateOffers: () => { void handleAlgorithmicOffers(); },
          setOffersToWorkups: () => {
            setKeepManuallySetOffers(true);
            setSetOffersToWorkupDialogOpen(true);
          },
          sendOffer: () => handleInitiateSendOffers(),
          cancelOffer: handleCancelOffer,
          uploadEvidence: () => setUploadEvidenceDialogOpen(true),
          printEvidence: () => setEvidencePrintDialogOpen(true),
          adjournCases: () => setAdjournDialogOpen(true),
          settleCase: () => {
            if (!selectedRows?.length) {
              toast.error('Select at least one case to settle');
              return;
            }
            setSettlingCases(true);
            setMultiSettlementOpen(true);
          },
          sendSettlement: () => setSettlementXlsDialogOpen(true),
          refreshAlgoOffers: () => { void handleRefreshAlgoOffers(); },
          training: openTrainingDialog,
          enterTownView: () => setTownView(true),
        }}
        disabled={{
          downloadCases: downloadsBlocked,
          downloadWorkups: downloadsBlocked,
          downloadStips: downloadsBlocked,
          markReviewed: isDisabled || areAllSelectedOffersFinalized || shouldShowCancelOffer || allCasesSettled,
          flagJurisdictional: isDisabled,
          flagCase: controlsLocked,
          generateOffers: true,
          setOffersToWorkups: controlsLocked || settingOffersToWorkup,
          sendOffer: isDisabled || !selectedCasesAreReviewed,
          cancelOffer: isDisabled || !shouldShowCancelOffer || stipsDownloadInFlight,
          uploadEvidence: isDisabled,
          adjournCases: isDisabled || hasSettledCasesSelected || hasUnfiledScarSelected || hasMissingHearingDateSelected,
          settleCase: isDisabled || hasSettledCasesSelected,
          sendSettlement: isDisabled || !allCasesSettled,
          refreshAlgoOffers: controlsLocked || generatingAlgorithmicOffers || isTrainingUser,
          training: controlsLocked,
        }}
        tooltips={{
          downloadCases: downloadsBlockedTitle,
          downloadWorkups: downloadsBlockedTitle,
          downloadStips: downloadsBlockedTitle,
          markReviewed: allCasesSettled ? 'Selected cases are already settled' : selectCasesTip,
          flagJurisdictional: selectCasesTip,
          sendOffer: !hasSelectedRows ? selectCasesTip : (!selectedCasesAreReviewed ? 'Mark cases reviewed before sending offers' : ''),
          uploadEvidence: selectCasesTip,
          adjournCases: !hasSelectedRows ? selectCasesTip
            : hasSettledCasesSelected ? 'Not available, settled cases are selected'
            : hasUnfiledScarSelected ? 'Only SCAR filed cases can be adjourned'
            : hasMissingHearingDateSelected ? 'Selected cases need a SCAR hearing date unless already open adjourned' : '',
          settleCase: hasSettledCasesSelected ? 'Not available, settled cases are selected' : selectCasesTip,
          sendSettlement: allCasesSettled ? '' : 'Select only settled cases to send settlement email',
          cancelOffer: selectCasesTip,
          training: casesStillRunning ? casesRunningTooltip : 'Training data available',
        }}
        labels={{
          downloadCases: selectedRows.length === 1 ? 'Download Case' : 'Download Cases',
          downloadWorkups: offerCountLabel('Download Workup', 'Download Workups'),
          downloadStips: offerCountLabel('Download Stip', 'Download Stips'),
          sendOffer: offerCountLabel('Send Offer', 'Send Offers'),
          settleCase: offerCountLabel('Settle Case', 'Settle Cases'),
          adjournCases: offerCountLabel('Open Adjourn Case', 'Open Adjourn Cases'),
          sendSettlement: offerCountLabel('Send Settlement', 'Send Settlements'),
          cancelOffer: hasSettledCasesSelected ? 'Cancel Settlement' : offerCountLabel('Cancel Offer', 'Cancel Offers'),
        }}
      />
      <DownloadButton
        ref={downloadRef}
        showTrigger={false}
        onLoadingChange={setDownloadLoading}
        caseData={caseData}
        fileName={fileName}
        selectedRows={selectedRows}
        townView={townView}
        villageMode={villageMode}
        rows={rows}
        isDataLoading={isDataLoading}
        courtDate={courtDate}
        avToggleActive={avToggleActive}
        isWorkupRegenerating={isWorkupRegenerating}
        casesStillRunning={casesStillRunning}
      />

      {/* Centralized Case Review Dialogs */}
      <CaseReviewDialogs
        // Missing Offers Dialog
        missingOffersDialogOpen={missingOffersDialogOpen}
        setMissingOffersDialogOpen={setMissingOffersDialogOpen}
        missingOffersList={missingOffersList}
        handleSetOffersToWorkup={handleSetOffersToWorkup}
        updatingOffersToWorkup={updatingOffersToWorkup}
        
        // Missing Comps Dialog (not used in CustomToolbar but required by component)
        missingCompsDialogOpen={false}
        setMissingCompsDialogOpen={() => {}}
        missingCompsList={[]}
        handleProceedWithMissingComps={() => {}}
        
        // Missing Workup Values Dialog
        missingWorkupDialogOpen={missingWorkupDialogOpen}
        setMissingWorkupDialogOpen={setMissingWorkupDialogOpen}
        missingWorkupList={missingWorkupList}

        workupsAboveIfmvDialogOpen={workupsAboveIfmvDialogOpen}
        setWorkupsAboveIfmvDialogOpen={setWorkupsAboveIfmvDialogOpen}
        workupsAboveIfmvList={workupsAboveIfmvList}
        handleContinueWorkupsAboveIfmv={handleContinueWorkupsAboveIfmv}
        
        // Subset Warning Dialog
        subsetWarningDialogOpen={subsetWarningDialogOpen}
        setSubsetWarningDialogOpen={setSubsetWarningDialogOpen}
        selectedRows={selectedRows}
        openCases={openCases}
        emailingOffers={emailingOffers}
        handleSendSelectedClick={handleSendSelectedClick}
        handleSendAllFinalized={handleSendAllFinalized}
        
        // Send Selected Confirmation Dialog
        sendSelectedConfirmDialogOpen={sendSelectedConfirmDialogOpen}
        setSendSelectedConfirmDialogOpen={setSendSelectedConfirmDialogOpen}
        handleConfirmSendSelected={handleConfirmSendSelected}
        
        // Unified Cancel Dialog
        cancellingOffers={cancellingOffers}
        rowsToCancel={rowsToCancel}
        unifiedCancelDialogOpen={unifiedCancelDialogOpen}
        setUnifiedCancelDialogOpen={closeCancelDialog}
        handleUnifiedCancelConfirm={handleUnifiedCancelConfirm}
        
        // Upload Dialog (not used in CustomToolbar but required by component)
        uploadDialogOpen={false}
        // setUploadDialogOpen={() => {}}
        handleFileUpload={() => {}}
        handleUpdateOffers={() => {}}
        townView={townView}
        maxRounds={maxRounds}
        
        // Offer Resend Dialog
        resendDialogOpen={resendDialogOpen}
        setResendDialogOpen={setResendDialogOpen}
        alreadySentCount={alreadySentCount}
        handleResendConfirm={handleResendConfirm}
      />
      


      
      {/* MUI Dialog for email preview */}
      {emailPreviewDialogComponent}

      {/* Settlement Email Dialog */}
      {SettlementEmailDialog}
      <IncludeSettlementXlsDialog
        open={settlementXlsDialogOpen}
        onClose={() => setSettlementXlsDialogOpen(false)}
        onConfirm={(includeXls) => {
          setSettlementXlsDialogOpen(false);
          setSendingSettlementEmail(true);
          void handleSettlementEmailWrapper(includeXls);
        }}
      />


      
      {/* Generalized Email Dialog for pre-processed data */}
      {GeneralizedEmailDialog}
      {TownPortalEmailDialog}

      {/* Multi-Settlement Dialog - only render when open */}
      {multiSettlementOpen && (
        <MultiSettlementDialog
          open={multiSettlementOpen}
          onClose={() => {
            setMultiSettlementOpen(false);
            setSettlingCases(false);
          }}
          rows={selectedRows}
        />
      )}

      <StableMuiDialog
        open={setOffersToWorkupDialogOpen}
        onClose={settingOffersToWorkup ? undefined : () => setSetOffersToWorkupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Offers to Workup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${selectedRows?.length ? `This will update ${setOffersToWorkupRows.length} selected parcel${setOffersToWorkupRows.length === 1 ? '' : 's'}` : `This will update all ${setOffersToWorkupRows.length} parcels`}. If an offer was already sent, it will create a new round. Otherwise it will update the current offer.`}
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2, ml: 0 }}
            control={
              <Checkbox
                checked={keepManuallySetOffers}
                onChange={(e) => setKeepManuallySetOffers(e.target.checked)}
              />
            }
            label="Keep manually set offers"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetOffersToWorkupDialogOpen(false)} variant="outlined" disabled={settingOffersToWorkup}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSetOffersToWorkup}
            color="primary"
            variant="contained"
            disabled={settingOffersToWorkup}
          >
            {settingOffersToWorkup ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </StableMuiDialog>

      <NyscefUploadModal
        open={uploadEvidenceDialogOpen}
        onClose={() => setUploadEvidenceDialogOpen(false)}
        selectedRows={selectedRows}
        rows={rows}
      />

      <EvidencePrintDialog
        open={evidencePrintDialogOpen}
        onClose={() => setEvidencePrintDialogOpen(false)}
        rows={displayedRows}
        selectedRows={selectedRows}
        onClearSelection={() => setSelectionModel(createEmptyRowSelection())}
        evidenceFileName={generateEvidenceFileName(
          getNegotiationObj?.MuniCode,
          getNegotiationObj?.TaxYear,
          getNegotiationObj?.CourtDate,
        )}
      />

      <DocRequestDialog
        open={docRequestDialogOpen}
        onClose={() => setDocRequestDialogOpen(false)}
        rows={selectedRows}
        negotiationObj={getNegotiationObj}
      />

      <AdjournCasesDialog
        open={adjournDialogOpen}
        onClose={() => setAdjournDialogOpen(false)}
        rows={selectedRows}
      />

      <TownPortalOffersDialog
        open={betaSendOffersDialogOpen}
        onClose={() => setBetaSendOffersDialogOpen(false)}
        onConfirm={handleBetaSendOffersConfirm}
        negotiationObj={getNegotiationObj}
        defaultWorkupReportType={workupReportType}
      />

      {/* Training Dialog */}
      <StableMuiDialog open={trainingDialogOpen} onClose={() => !trainingDialogLoading && setTrainingDialogOpen(false)}>
        <DialogTitle>Update Data with Training</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hasTrainingComps && hasTrainingOffers && 'There are Training Comps, and Training Offers.'}
            {hasTrainingComps && !hasTrainingOffers && 'There are training comps'}
            {!hasTrainingComps && hasTrainingOffers && 'There are Training Offers'}
          </DialogContentText>
          
          {/* Training user information */}
          {hasTrainingComps && trainingUsersInfo.count > 0 && (
            <Box sx={{ mt: 2, mb: 1 }}>
              {trainingUsersInfo.count === 1 ? (
                <DialogContentText>
                  Training comps saved by: <strong>{trainingUsersInfo.userNames[0]}</strong>
                </DialogContentText>
              ) : (
                <>
                  <DialogContentText sx={{ mb: 2, fontWeight: 'medium' }}>
                    <strong>Multiple training users detected. Please select which user's comps to use:</strong>
                  </DialogContentText>
                  <RadioGroup
                    value={selectedTrainingUserId || ''}
                    onChange={(e) => setSelectedTrainingUserId(Number(e.target.value))}
                  >
                    {trainingUsersInfo.userIds.map((userId, index) => (
                      <FormControlLabel
                        key={userId}
                        value={userId}
                        control={<Radio />}
                        label={trainingUsersInfo.userNames[index]}
                      />
                    ))}
                  </RadioGroup>
                </>
              )}
            </Box>
          )}
          
          <DialogContentText
            sx={{
              color: '#d32f2f',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              mt: 2,
              border: '1px solid #d32f2f',
              borderRadius: 1,
              p: 1.5,
              backgroundColor: '#ffebee'
            }}
          >
            THIS WILL OVERWRITE MANUALLY REVIEWED COMPS
          </DialogContentText>
          
          {/* Checkbox to update offers as well */}
          {hasTrainingOffers && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateOffersToo}
                    onChange={(e) => setUpdateOffersToo(e.target.checked)}
                    disabled={trainingDialogLoading}
                  />
                }
                label="Also update training offers"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 1, px: 3, pb: 2 }}>
          <Button
            variant="contained"
            disabled={!hasTrainingComps || trainingDialogLoading || (trainingUsersInfo.count > 1 && !selectedTrainingUserId)}
            startIcon={trainingDialogLoading && <CircularProgress size={16} />}
            onClick={async () => {
              try {
                setTrainingDialogLoading(true);
                const cases = Array.isArray(getNegotiationObj?.cases) ? getNegotiationObj.cases : (Array.isArray(caseData) ? caseData : []);
                logger.log('Training comps grouped by case:', trainingCompsByCase);
                
                // Save comps FIRST to avoid having offers cleared by the comp save operation
                const result = await batchSaveComps({
                  negotiationObj: getNegotiationObj,
                  cases,
                  user,
                  options: {
                    trainingCompsByCase,
                    isTrainingOverwrite: true
                  }
                });
                
                logger.log('Training comps save result:', result);
                
                // Update training offers AFTER comps are saved (only if user selected the option)
                if (updateOffersToo) {
                  // Filter training offers by selected user if multiple users exist (same as comps)
                  const filterByUserId = trainingUsersInfo.count > 1 && selectedTrainingUserId 
                    ? selectedTrainingUserId 
                    : null;
                  
                  // Update training offers: Get ALL training offers for each case with training comps
                  const trainingOffersList = collectTrainingOffersList(filterByUserId);
                  const casesWithTrainingComps = Object.keys(trainingCompsByCase).filter(pid => 
                    trainingCompsByCase[pid]?.Comps
                  );
                  
                  // Collect ALL training offers for each case that has training comps
                  const offersToUpdate = [];
                  casesWithTrainingComps.forEach(parcelId => {
                    const caseTrainingOffers = trainingOffersList.filter(o => o.parcel_id === parcelId);
                    // Add ALL training offers for this case
                    caseTrainingOffers.forEach(offer => {
                      offersToUpdate.push({
                        parcelId: parcelId,
                        offerValue: offer.Offer, // AV value from training offer
                        offeredBy: offer.OfferedBy || 'Aventine',
                        offerDate: offer.OfferDate || new Date().toISOString(),
                        comments: offer.Comments || '',
                        response: offer.Response || null
                      });
                    });
                  });
                  
                  logger.log('🔄 [TRAINING OFFERS] Batch updating all training offers for cases with training comps:', {
                    totalCasesWithTrainingComps: casesWithTrainingComps.length,
                    totalOffersToUpdate: offersToUpdate.length,
                    offersByParcel: offersToUpdate.reduce((acc, o) => {
                      if (!acc[o.parcelId]) acc[o.parcelId] = 0;
                      acc[o.parcelId]++;
                      return acc;
                    }, {})
                  });
                  
                  // Batch update all offers in a single call (outside the loop)
                  if (offersToUpdate.length > 0) {
                    try {
                      const context = createOfferContext(user, getNegotiationObj, 'Manual');
                      const offersResult = await updateOffersBatch(offersToUpdate, context, {
                        liveOffer: 0 // Training offers are not live
                      });
                      logger.log('✅ [TRAINING OFFERS] All offers updated successfully in batch:', offersResult);
                    } catch (offerError) {
                      logger.error('❌ [TRAINING OFFERS] Failed to batch update offers:', offerError);
                      // Don't fail the whole operation if offer update fails
                    }
                  }
                }
                
                // Update negotiation object AFTER both comps and offers are saved
                // This ensures the frontend state reflects both changes
                if (result && result.results && result.casesToSave) {
                  // Get the latest negotiation object (it may have been updated by offer updates)
                  const currentNegotiationObj = usePersistedDataStore.getState().negotiationObj;
                  const updatedNegotiationObj = {
                    ...currentNegotiationObj,
                    cases: currentNegotiationObj.cases.map((caseItem, index) => {
                      // Check if this case was in the saved batch
                      const savedCaseInfo = result.casesToSave.find((c, savedIdx) => 
                        c.originalIndex === index && result.results[savedIdx] === true
                      );
                      
                      if (savedCaseInfo) {
                        const trainingData = trainingCompsByCase[caseItem.parcel_id];
                        if (trainingData && trainingData.Comps) {
                          return {
                            ...caseItem,
                            Comps: trainingData.Comps,
                            RunDate: new Date().toUTCString(),
                            ManualReview: 0,
                            ReviewedBy: user.userId
                          };
                        }
                      }
                      return caseItem;
                    }),
                  };
                  setNegotiationObj(updatedNegotiationObj);
                  setNegotiationObjVersion(prev => prev + 1);
                }
                
                toast.success('Training comps saved successfully');
                setTrainingDialogOpen(false);
              } catch (error) {
                logger.error('Error saving training comps:', error);
                toast.error(`Failed to save training comps: ${error.message}`);
              } finally {
                setTrainingDialogLoading(false);
              }
            }}
          >
            Overwrite Data
          </Button>
          
          <Button
            variant="outlined"
            disabled={trainingDialogLoading}
            onClick={() => {
              setTrainingDialogOpen(false);
              setSelectedTrainingUserId(null);
              setUpdateOffersToo(false);
            }}
          >
            Cancel
          </Button>

        </DialogActions>
      </StableMuiDialog>

    </Toolbar>
  );
}

export default CustomToolbar; 